"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { PageLoading } from "@/components/page-loading";
import { Button } from "@/components/ui/button";
import { useAuth, useSession } from "@/lib/auth";
import { BillingPageContent } from "./billing.content";

/** No shared `NEXT_PUBLIC_*` support URL in repo; use mailto until product adds one. */
const BILLING_SUPPORT_HREF = "mailto:support@radium.cloud";

export default function BillingPage() {
  const { data: session, isPending } = useSession();
  const { status: authStatus, meErrorSummary } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isContentLoading, setIsContentLoading] = useState(true);

  useEffect(() => {
    const willRedirect = !isPending && !session?.user && authStatus === "unauthenticated";
    if (willRedirect) {
      router.push("/login");
    }
  }, [session, isPending, router, authStatus]);

  if (!session?.user && !isPending) {
    if (authStatus === "unauthenticated") {
      return null;
    }
    if (authStatus === "error") {
      return (
        <div className="@container/main flex min-h-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col items-center justify-center p-4 md:p-8">
            <div className="w-full max-w-md space-y-4 text-center">
              <h2 className="text-lg font-semibold tracking-tight">We could not load your account</h2>
              <p className="text-muted-foreground text-sm">Refresh the page and try again, or contact support.</p>
              {meErrorSummary ? (
                <p className="text-muted-foreground border-border bg-muted/40 rounded-md border px-3 py-2 text-left font-mono text-xs wrap-break-word">
                  {meErrorSummary}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  We could not determine the exact error. Please try again in a moment.
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void queryClient.invalidateQueries({ queryKey: ["me"] });
                  }}
                >
                  Retry
                </Button>
                <Button type="button" variant="outline" asChild>
                  <a href={BILLING_SUPPORT_HREF}>Contact support</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex-1 space-y-4">
        {isPending || isContentLoading ? <PageLoading variant="content" /> : null}
        <div className={isPending || isContentLoading ? "opacity-0 pointer-events-none h-0 overflow-hidden" : ""}>
          <BillingPageContent onLoadingChange={setIsContentLoading} />
        </div>
      </div>
    </div>
  );
}
